import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider, IconButton,
  Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Operator panel to manage who receives ClickHouse operational-health alert
 * emails (disk pressure, node down, WAL dead-lettered, backup/retention
 * failures). The backend scheduler sends automatically when the fleet turns
 * unhealthy; this only curates the recipient list and offers a test send.
 */
const ClickHouseAlertRecipients = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'err', text }
  const [testingId, setTestingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingService.alertRecipients();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.error || 'Failed to load recipients' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg(null), 5000); };

  const add = async () => {
    const e = email.trim();
    if (!EMAIL_RE.test(e)) { flash('err', 'Enter a valid email address'); return; }
    setBusy(true);
    try {
      await billingService.alertRecipientAdd(e);
      setEmail('');
      await load();
      flash('ok', `Added ${e}`);
    } catch (err) {
      flash('err', err?.response?.data?.error || 'Could not add recipient');
    } finally { setBusy(false); }
  };

  const toggle = async (r) => {
    try {
      await billingService.alertRecipientToggle(r.id, !r.enabled);
      setRows(rows.map(x => (x.id === r.id ? { ...x, enabled: !r.enabled } : x)));
    } catch {
      flash('err', 'Could not update recipient');
    }
  };

  const remove = async (r) => {
    try {
      await billingService.alertRecipientDelete(r.id);
      setRows(rows.filter(x => x.id !== r.id));
      flash('ok', `Removed ${r.email}`);
    } catch {
      flash('err', 'Could not remove recipient');
    }
  };

  const test = async (addr, id) => {
    setTestingId(id ?? 'new');
    try {
      await billingService.alertRecipientTest(addr);
      flash('ok', `Test email sent to ${addr}`);
    } catch (err) {
      flash('err', err?.response?.data?.error || 'Test send failed (is email configured in Settings?)');
    } finally { setTestingId(null); }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <EmailIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            Health alert recipients
          </Typography>
          <Chip size="small" label={`${rows.filter(r => r.enabled).length} active`} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          These addresses are emailed when ClickHouse becomes unhealthy — disk pressure (keep-free-space
          budget), a node down, WAL dead-lettered, or backup/retention failures. Repeat mails for the same
          condition are suppressed for a few hours, and a recovery notice is sent when it clears.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            size="small" fullWidth placeholder="ops@yourcompany.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            disabled={busy}
          />
          <Button
            variant="outlined" startIcon={<SendIcon />}
            disabled={!EMAIL_RE.test(email.trim()) || testingId === 'new'}
            onClick={() => test(email.trim(), null)}
          >
            Test
          </Button>
          <Button
            variant="contained" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={add} disabled={busy || !EMAIL_RE.test(email.trim())}
          >
            Add
          </Button>
        </Stack>

        {msg && (
          <Typography variant="caption"
                      color={msg.kind === 'ok' ? 'success.main' : 'error.main'}
                      sx={{ display: 'block', mb: 1 }}>
            {msg.text}
          </Typography>
        )}

        <Divider sx={{ mb: 1 }} />

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No recipients yet. Add an address above to start receiving ClickHouse health alerts.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {rows.map((r) => (
              <Stack key={r.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.75 }}>
                <Tooltip title={r.enabled ? 'Enabled — will receive alerts' : 'Disabled — kept but skipped'}>
                  <Switch size="small" checked={r.enabled} onChange={() => toggle(r)} />
                </Tooltip>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, opacity: r.enabled ? 1 : 0.55 }} noWrap>
                    {r.email}
                  </Typography>
                  {r.createdBy && (
                    <Typography variant="caption" color="text.secondary">added by {r.createdBy}</Typography>
                  )}
                </Box>
                <Tooltip title="Send a test email to this address">
                  <span>
                    <IconButton size="small" onClick={() => test(r.email, r.id)} disabled={testingId === r.id}>
                      {testingId === r.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => remove(r)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ClickHouseAlertRecipients;
